import React, { useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import PageSEO from '../components/PageSEO';
import SiteHeader from '../components/SiteHeader';
import SiteFooter from '../components/SiteFooter';
import { COMPLIANCE_COPY, SMS_DISPLAY_NUMBER, SMS_NUMBER } from '../components/landing/constants';

/** A friend's invite link opens Messages with their code already typed; texting it signs up and credits both. */
const ReferralInvitePage: React.FC = () => {
  const { code: raw = '' } = useParams();
  const code = /^[A-Za-z0-9]{2,32}$/.test(raw) ? raw.toUpperCase() : '';
  const body = code ? `Hi Aspen! My friend invited me (code ${code})` : 'Hey Aspen';
  const smsLink = `sms:${SMS_NUMBER}?&body=${encodeURIComponent(body)}`;

  useEffect(() => {
    // On a phone, go straight to Messages; the page stays as the fallback.
    if (/iPhone|iPad|iPod|Android/i.test(navigator.userAgent)) window.location.href = smsLink;
  }, [smsLink]);

  return <div className="min-h-screen bg-aspen-warm-ivory dark:bg-aspen-night-sage flex flex-col">
    <PageSEO title="You’re invited to Aspen" description="A friend invited you to Aspen, the AI you text. Send one text to start." canonicalPath="/r" noindex />
    <SiteHeader />
    <main className="mx-auto w-full max-w-xl flex-1 px-5 py-16 text-center sm:px-8">
      <p className="font-semibold uppercase tracking-[0.2em] text-aspen-sage-teal">You’re invited</p>
      <h1 className="mt-4 text-4xl font-bold text-aspen-deep-eucalyptus dark:text-aspen-pearl">A friend wants you to meet Aspen</h1>
      <p className="mt-4 text-lg leading-8 text-aspen-slate-blue dark:text-aspen-lavender-gray">Aspen is an AI you text to think things through, remember the details, and get reminders. No app, no sign-up form. Send one text and you’re in{code ? ', and you both get bonus texts' : ''}.</p>
      <a href={smsLink} className="mt-8 inline-flex min-h-[48px] items-center justify-center rounded-xl bg-aspen-sage-teal px-6 py-3 text-lg font-semibold text-white">Text Aspen</a>
      {code && <p className="mt-6 text-aspen-slate-blue dark:text-aspen-lavender-gray">Or text <strong className="select-all font-mono">{code}</strong> to <a href={smsLink} className="font-semibold text-aspen-sage-teal underline">{SMS_DISPLAY_NUMBER}</a>.</p>}
      <p className="mt-6 text-sm text-aspen-slate-blue dark:text-aspen-lavender-gray">On a computer? <Link to={code ? `/account?ref=${code}` : '/account'} className="text-aspen-sage-teal underline">Sign up on the web</Link>.</p>
      <p className="mt-10 text-xs leading-5 text-aspen-slate-blue/80 dark:text-aspen-lavender-gray/80">{COMPLIANCE_COPY}</p>
    </main>
    <SiteFooter />
  </div>;
};

export default ReferralInvitePage;
